import { useState } from 'react';
import { questionQualityApi } from '../../../api/questionQualityApi';
import type { Resolution, ResolutionStatus } from '../../../api/questionQualityApi';
import { DERIVED_STATUSES, RESOLUTION_LABEL, RESOLUTION_STATUSES } from '../../../api/questionQualityApi';
import { resolutionColor } from './verdictDisplay';

/**
 * What a human decided about ONE finding (question × rule) — the only thing a
 * reader can write to the quality ledger.
 *
 * Per finding rather than per question because a question failing three rules is
 * three findings, and "the MCQ key is fine but the solution really doesn't reach
 * the answer" is the ordinary case. Status and comment save together in one call:
 * a status with no reason beside it is what makes a later reader re-open a
 * question that was already settled.
 */

export function ResolutionBadge({ status, title }: { status: ResolutionStatus; title?: string }) {
    return (
        <span
            className="qq-respill"
            style={{ color: resolutionColor[status], borderColor: resolutionColor[status] }}
            title={title}
        >{RESOLUTION_LABEL[status]}</span>
    );
}

export function ResolutionEditor({ subject, questionId, ruleId, resolution, aiFixable, onSaved }: {
    subject: string;
    questionId: string;
    ruleId: string;
    resolution?: Resolution;
    /** Whether this rule's findings may be routed to the AI, and why not when
     *  they may not. Absent (an older backend) leaves the option enabled — the
     *  endpoint refuses it either way. */
    aiFixable?: { allowed: boolean; reason: string };
    /** Hands the saved value back so the row above updates without a refetch. */
    onSaved: (next: Resolution | undefined) => void;
}) {
    const [status, setStatus] = useState<ResolutionStatus>(resolution?.status ?? 'active');
    const [comment, setComment] = useState(resolution?.comment ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const dirty = status !== (resolution?.status ?? 'active')
        || comment.trim() !== (resolution?.comment ?? '');

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const { resolution: next } = await questionQualityApi.setResolution({
                subject, questionId, ruleId, status, comment: comment.trim() || undefined,
            });
            onSaved(next);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save');
        } finally { setSaving(false); }
    };

    return (
        <div className="qq-reseditor">
            {/* "Verified fixed" is disabled outright: it means a re-judge of the
                edited question passed, which is a sentence only the sweep is in
                a position to write, and letting someone claim it would collapse
                the distinction from "Fixed" that it exists to make. The endpoint
                refuses it too.

                "Fix needed by AI" is disabled on a rule the fixer would refuse
                — a curriculum judgement, or a defect no text edit can repair.
                Offering it would take a request nobody can serve: the worklist
                drops the finding and writes nothing back, so it would sit
                flagged, indistinguishable from one that is genuinely queued.
                The reason rides on the option's title, because "why not?" is
                the immediate next question. */}
            <select value={status} onChange={e => setStatus(e.target.value as ResolutionStatus)}>
                {RESOLUTION_STATUSES.map(s => {
                    const derived = DERIVED_STATUSES.includes(s);
                    const blocked = derived || (s === 'fix-needed-by-ai' && aiFixable && !aiFixable.allowed);
                    const why = derived
                        ? 'Set by the sweep when a re-check of the edited question passes'
                        : aiFixable?.reason;
                    return (
                        <option key={s} value={s} disabled={blocked} title={blocked ? why : undefined}>
                            {RESOLUTION_LABEL[s]}
                            {derived ? ' — set by the sweep' : blocked ? ' — not for this rule' : ''}
                        </option>
                    );
                })}
            </select>
            <input
                type="text"
                value={comment}
                placeholder="Why — what you checked, or what you changed"
                onChange={e => setComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && dirty) void save(); }}
            />
            {/* Explicit, because this writes to the audit trail. Enabled only when
                there is something to write. */}
            <button type="button" disabled={!dirty || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
            </button>
            {resolution?.at && !dirty && (
                <span className="qq-dim">
                    {/* The name when we have one; the raw id only when we do
                        not. A truncated GUID identifies nobody. */}
                    {resolution.by ? `${resolution.byName ?? resolution.by.slice(0, 8)} · ` : ''}
                    {new Date(resolution.at).toLocaleDateString()}
                </span>
            )}
            {error && <span className="qq-warn">{error}</span>}
        </div>
    );
}
